/**
 * @file: useConstructionReportModels.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { toMediaObject } from '@euler/app/flows/report/functions';
import { getDefectiveLevel } from '@euler/functions/getDefectiveLevel';
import { DefectiveLevel, MediaObject } from '@euler/model';
import { FaultInspectionItem } from '@euler/model/FaultInspectionItem';
import { InspectionTaskJobStatus } from '@euler/model/enum';
import {
  ConstructionReportItemDetail,
  ReportProductReferralItemConfirmation,
  VehicleReport,
} from '@euler/model/report';
import { comparer, comparerDesc } from '@euler/utils';
import { array2map } from '@euler/utils/array';
import { useMemo } from 'react';

export type ConstructionCellModel = {
  index: number;
  jobId: number;
  name: string;
  technicianName: string;
  count: number;
  timestamp: string | Date;
  remark?: string;
  status: InspectionTaskJobStatus;
  details: ConstructionItemDetailModel[];
  procedures: ConstructionProcedureModel[];
  productConfirmations: ReportProductReferralItemConfirmation[] | undefined;
};

type ConstructionItemDetailModel = {
  title: string;
  defectiveLevel: DefectiveLevel;
};

type ConstructionProcedureModel = {
  name: string;
  medias: MediaObject[];
};

export function useConstructionReportModels(
  report: VehicleReport,
  options?: {
    targetJobId?: number;
    faultLikeSubject?: FaultInspectionItem;
  },
) {
  const targetJobId = options?.targetJobId;
  return useMemo(() => {
    const cells: ConstructionCellModel[] = [];

    const items = (
      (targetJobId
        ? report.construction?.items.filter(x => x.jobId === targetJobId)
        : report.construction?.items) ?? []
    ).filter(item => {
      return (
        options?.faultLikeSubject == null ||
        options.faultLikeSubject.relatedJobs?.some(
          relatedJob => relatedJob.jobName === item.name,
        )
      );
    });

    const productReferrals = report.productReferrals ?? [];
    const productReferralMapBySubject = array2map(
      productReferrals,
      x => x.subject,
    );

    items.forEach((item, index) => {
      const medias = (item.medias || []).map(x => {
        if (x.procedure) return x;

        const media = { ...x };
        if (!media.procedure) {
          if (media.category === 'procedure') {
            media.procedure = '施工过程';
            media.procedureOrder = -1;
          } else if (media.category === 'comparison') {
            media.procedure = '施工对比';
            media.procedureOrder = -2;
          }
          media.isLegacy = true;
        }
        return media;
      });

      medias.sort((x, y) =>
        comparer(x.procedureOrder ?? 0, y.procedureOrder ?? 0),
      );

      const details: ConstructionItemDetailModel[] = item.details.map(
        detail => ({
          title: constructionDetailItemTitle(detail),
          defectiveLevel: getDefectiveLevel(
            detail.abnormalLevel,
            detail.severityLevel,
          ),
        }),
      );

      const procedures: ConstructionProcedureModel[] = [];
      for (const media of medias) {
        const name = media.procedure!;
        let procedure = procedures.find(x => x.name === name);
        if (!procedure) {
          procedure = { name, medias: [] };
          procedures.push(procedure);
        }
        const obj = toMediaObject(media);
        if (!obj.subTitle) {
          obj.subTitle = media.procedure ?? undefined;
        }
        procedure.medias.push(obj);
      }

      cells.push({
        index,
        jobId: item.jobId,
        name: item.name,
        status: item.status ?? InspectionTaskJobStatus.Finished,
        technicianName:
          item.technicianName ?? report.construction?.technicianName ?? '',
        count: medias.length,
        details,
        procedures,
        timestamp: item.finishedAt ?? item.createdAt ?? new Date(),
        remark: item.remark ?? undefined,
        productConfirmations: productReferralMapBySubject.get(item.name)
          ?.confirmations,
      });
    });

    cells.sort((x, y) => comparerDesc(x.count, y.count));
    cells.forEach((cell, index) => (cell.index = index));

    return { cells };
  }, [
    options?.faultLikeSubject,
    report.construction?.items,
    report.construction?.technicianName,
    report.productReferrals,
    targetJobId,
  ]);
}

function constructionDetailItemTitle(detail: ConstructionReportItemDetail) {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const [x, y] = [detail.itemName || detail.siteName || '', detail.label || ''];
  if (!x) return y;
  if (y.startsWith(x)) {
    return [x, y.substring(x.length).replace(/^\s*[:：]\s*/, '')].join(': ');
  }
  const m = /^(.*?[:：]\s*)/.exec(y);
  if (m) {
    return [x, y.substring(m[1].length)].join(': ');
  }
  return [x, y].join(': ');
}
